import { useNatsStore } from '@omniflow/browser-bus'
import { AccessRoleRoutes, AccessRoutes } from '@omniflow/contracts-access'
import type { InputOf, MessageFields, ReplyOf } from '@omniflow/contracts-core'

// ── Типы — из контракта (эпик ACCESS), не дублируем вручную ──

type RoleListReply = ReplyOf<typeof AccessRoleRoutes.list>
export type RoleRow = Extract<RoleListReply, { isOk: true }>['items'][number]
export type RoleInput = Omit<InputOf<typeof AccessRoleRoutes.create>, MessageFields>
export type RolePatch = InputOf<typeof AccessRoleRoutes.update>['patch']
export type AccessCatalog = ReplyOf<typeof AccessRoutes.catalog>
export type AccessCatalogResource = AccessCatalog['resources'][number]
export type AccessMe = Extract<ReplyOf<typeof AccessRoutes.me>, { isOk: true }>

/** Ошибка команды роли: код из контракта (`not_managed`, `missing_requires`…) — UI объясняет его человеку. */
export class RoleCommandError extends Error {
  constructor(
    readonly errorCode: string,
    message: string,
  ) {
    super(message)
  }
}

const fail = (subject: string, reply: { errorCode: string; error?: string | undefined }): never => {
  throw new RoleCommandError(reply.errorCode, `${subject} failed: ${reply.errorCode}${reply.error ? ` (${reply.error})` : ''}`)
}

export const fetchRoles = async (): Promise<RoleRow[]> => {
  const { requireBus } = useNatsStore()
  const reply = await requireBus().request(AccessRoleRoutes.list, { limit: 1000 })
  if (!reply.isOk) return fail('q.access.role.list', reply)
  return [...reply.items].sort((a, b) => a.name.localeCompare(b.name, 'ru'))
}

export const createRole = async (dto: RoleInput): Promise<RoleRow> => {
  const { requireBus } = useNatsStore()
  const reply = await requireBus().request(AccessRoleRoutes.create, dto)
  if (!reply.isOk) return fail('cmd.access.role.create', reply)
  return reply.item
}

/**
 * `expectedUpdatedAt` — роль записывается, только если её никто не правил с момента, как
 * её прочитали (CRUDCAS): иначе отказ `conflict`, а не молча затёртая чужая правка.
 */
export const updateRole = async (id: string, patch: RolePatch, expectedUpdatedAt?: string): Promise<void> => {
  const { requireBus } = useNatsStore()
  const reply = await requireBus().request(AccessRoleRoutes.update, {
    id,
    patch,
    ...(expectedUpdatedAt ? { where: { field: 'updatedAt' as const, op: 'eq' as const, value: expectedUpdatedAt } } : {}),
  })
  if (!reply.isOk) fail('cmd.access.role.update', reply)
}

export const deleteRole = async (id: string): Promise<void> => {
  const { requireBus } = useNatsStore()
  const reply = await requireBus().request(AccessRoleRoutes.delete, { id })
  if (!reply.isOk) fail('cmd.access.role.delete', reply)
}

export const fetchAccessCatalog = async (): Promise<AccessCatalog> => {
  const { requireBus } = useNatsStore()
  return requireBus().request(AccessRoutes.catalog, {})
}

export const fetchAccessMe = async (): Promise<AccessMe> => {
  const { requireBus } = useNatsStore()
  const reply = await requireBus().request(AccessRoutes.me, {})
  if (!reply.isOk) return fail('q.access.me', reply)
  return reply
}
